import type {
  MasteryMapDocument,
  MasteryMapDomain,
  MasteryMapStandard,
  MasteryMapStat,
  MasteryMapStatParts,
} from '@ts-mastery-map/interfaces'
import type { MasteryMapTone } from '@ts-mastery-map/types'

import { MasteryLevel } from '@ts-mastery-map/enums'

const LEVEL_LABELS: Record<MasteryLevel, string> = {
  [MasteryLevel.Mastered]: 'Proficient',
  [MasteryLevel.Developing]: 'Developing',
  [MasteryLevel.NeedsSupport]: 'Needs Focus',
  [MasteryLevel.NotAssessed]: 'Not assessed',
}

const LEVEL_MODIFIERS: Record<MasteryLevel, string> = {
  [MasteryLevel.Mastered]: 'emerald',
  [MasteryLevel.Developing]: 'amber',
  [MasteryLevel.NeedsSupport]: 'red',
  [MasteryLevel.NotAssessed]: 'slate',
}

export const NOT_ASSESSED_STAT_KEY = 'not_assessed'

const STAT_VALUE_PATTERN = /^(?<count>\S+)(?:\s*\((?<share>[^)]+)\))?$/

const STAT_TONES: Record<string, MasteryMapTone> = {
  [MasteryLevel.Mastered]: 'emerald',
  [MasteryLevel.Developing]: 'amber',
  [MasteryLevel.NeedsSupport]: 'red',
}

export const getStatTone = (key: string): MasteryMapTone => STAT_TONES[key] ?? 'neutral'

export const getStatLabel = (stat: MasteryMapStat): string => (stat.key === 'total' ? 'Total' : stat.label)

export const getStatParts = (stat: MasteryMapStat): MasteryMapStatParts => {
  const groups = STAT_VALUE_PATTERN.exec(stat.value.trim())?.groups

  return {
    count: groups?.count ?? stat.value,
    share: groups?.share ?? null,
  }
}

export const getMasteryLevelLabel = (level: MasteryLevel): string => LEVEL_LABELS[level] ?? ''

export const getMasteryLevelModifier = (level: MasteryLevel): string => LEVEL_MODIFIERS[level] ?? 'slate'

export const isMeasuredStandard = (standard: MasteryMapStandard): boolean =>
  standard.level !== MasteryLevel.NotAssessed

export const getDomainTitle = (domain: MasteryMapDomain | null): string =>
  domain?.label ?? domain?.code ?? ''

const SHORT_DOMAIN_LABELS: Record<string, string> = {
  OA: 'Operations & algebraic thinking',
  NBT: 'Base ten',
  NF: 'Fractions',
  MD: 'Measurement & data',
  G: 'Geometry',
  RI: 'Informational text',
  RF: 'Foundational skills',
  SL: 'Speaking & listening',
}

/** `3.OA` -> `OA`: the code carries the grade prefix, the short label is keyed by the token alone. */
export const getDomainShortTitle = (domain: MasteryMapDomain): string =>
  SHORT_DOMAIN_LABELS[domain.code.split('.')[1] ?? ''] ?? getDomainTitle(domain)

export const findDomain = (
  document: MasteryMapDocument | null,
  code: string | null,
): MasteryMapDomain | null =>
  document?.domains.find(domain => domain.code === code) ?? null

const byCode = (a: MasteryMapStandard, b: MasteryMapStandard): number => a.code.localeCompare(b.code)

const byPercentageDesc = (a: MasteryMapStandard, b: MasteryMapStandard): number =>
  b.percentage - a.percentage || byCode(a, b)

/** Measured standards from best to worst; the ones the assessment measured nothing for are left out. */
export const sortStandards = (standards: MasteryMapStandard[]): MasteryMapStandard[] =>
  standards.filter(isMeasuredStandard).sort(byPercentageDesc)
